import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useImpersonatedTenant } from "../lib/impersonation";
import { TicketDisplay } from "../components/TicketDisplay";
import { LeafMark } from "../components/LeafMark";

type Tenant = {
  tenantId: string;
  name: string;
  themePrimary: string;
  logoUrl: string | null;
};

type LoyaltyConfig = {
  ticketsForReward: number;
  rewardLabel: string;
  rewardItem: string | null;
};

/**
 * Vista previa del Home del cliente, con branding real del tenant + data mock.
 * Acceso: tenant_admin o platform_admin. NO modifica nada en Firestore.
 * - tenant_admin: usa su propio claims.tenantId
 * - platform_admin: puede pasar ?tenant=X para previsualizar otro
 */
export function PreviewCustomer() {
  const nav = useNavigate();
  const { claims } = useAuth();
  const [params] = useSearchParams();
  const queryTenant = params.get("tenant");
  const impersonated = useImpersonatedTenant();
  const targetTenantId =
    claims.role === "platform_admin"
      ? queryTenant ?? impersonated ?? claims.tenantId
      : claims.tenantId;

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loyalty, setLoyalty] = useState<LoyaltyConfig | null>(null);
  const [mockTickets, setMockTickets] = useState(3);
  const [previewState, setPreviewState] = useState<"normal" | "complete" | "voucher">("normal");

  useEffect(() => {
    if (!targetTenantId) return;
    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(doc(db, "tenants", targetTenantId), (snap) => {
        const x = snap.data();
        if (!x) return;
        setTenant({
          tenantId: targetTenantId,
          name: (x.name as string) ?? targetTenantId,
          themePrimary: (x.theme?.primary as string) ?? "#4a6921",
          logoUrl: (x.theme?.logoUrl as string | null) ?? null
        });
      })
    );

    unsubs.push(
      onSnapshot(
        doc(db, "tenants", targetTenantId, "config", "loyalty"),
        (snap) => {
          const x = snap.data();
          setLoyalty({
            ticketsForReward: (x?.ticketsForReward as number) ?? 5,
            rewardLabel: (x?.rewardLabel as string) ?? "Cafe gratis",
            rewardItem: (x?.rewardItem as string | null) ?? null
          });
        }
      )
    );

    return () => unsubs.forEach((f) => f());
  }, [targetTenantId]);

  if (!targetTenantId) {
    return (
      <main className="min-h-dvh bg-cream">
        <div className="container-page text-center">
          <p className="text-sm text-leaf-700">No tienes acceso a preview de cliente.</p>
          <button onClick={() => nav(-1)} className="btn-primary mt-4">
            Volver
          </button>
        </div>
      </main>
    );
  }

  if (!tenant || !loyalty) {
    return (
      <main className="min-h-dvh bg-cream">
        <div className="container-page text-center text-leaf-700">Cargando preview…</div>
      </main>
    );
  }

  const isComplete = previewState === "complete";
  const showVoucher = previewState === "voucher";
  const ticketsShow = isComplete ? loyalty.ticketsForReward : mockTickets;

  return (
    <div className="min-h-dvh bg-cream pb-32">
      {/* Banner preview - sticky top */}
      <div className="sticky top-0 z-30 bg-amber-400 px-4 py-2 text-center text-sm font-semibold text-amber-950 shadow-md">
        <div className="mx-auto flex max-w-md items-center justify-between gap-2">
          <span>Vista previa · datos no reales</span>
          <button
            onClick={() => nav(claims.role === "platform_admin" ? "/platform" : "/admin")}
            className="rounded-full bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-900"
          >
            Volver al panel
          </button>
        </div>
      </div>

      {/* Top bar fake (no logout) */}
      <header
        className="flex items-center gap-3 border-b px-5 py-3"
        style={{ borderColor: tenant.themePrimary + "22" }}
      >
        <div style={{ color: tenant.themePrimary }}><LeafMark size={24} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-leaf-600">FIDELIZA</p>
          <p className="truncate font-display text-base font-semibold text-leaf-950">
            {tenant.name}
          </p>
        </div>
        <div
          className="grid h-9 w-9 place-items-center rounded-full font-display text-sm font-semibold text-white"
          style={{ backgroundColor: tenant.themePrimary }}
        >
          C
        </div>
      </header>

      <main className="bg-cream pt-4">
        <div className="mx-auto flex max-w-md flex-col gap-5 px-5">
          <header>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Bienvenido</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-leaf-950">
              Hola, Cliente Demo
            </h1>
          </header>

          {/* Loyalty card con branding del tenant */}
          <section
            className="overflow-hidden rounded-3xl p-5 shadow-soft ring-1"
            style={{
              background: `linear-gradient(135deg, ${tenant.themePrimary}f0, ${tenant.themePrimary}d0)`,
              borderColor: `${tenant.themePrimary}55`,
              color: "#ffffff"
            }}
          >
            <p className="text-xs uppercase tracking-wider text-white/80">Tu loyalty card</p>
            <h2 className="mt-1 font-display text-2xl font-semibold">{tenant.name}</h2>
            <p className="mt-1 text-sm text-white/90">
              {isComplete
                ? "¡Tu reward esta listo para canjear!"
                : `Junta ${loyalty.ticketsForReward} compras y gana 1 gratis.`}
            </p>
            <div className="mt-4">
              <TicketDisplay
                count={ticketsShow}
                max={loyalty.ticketsForReward}
                rewardLabel={loyalty.rewardLabel}
              />
            </div>
            {isComplete && (
              <button
                disabled
                className="btn-primary mt-4 w-full bg-white text-leaf-800 opacity-90 cursor-not-allowed"
              >
                Canjear mi reward
              </button>
            )}
          </section>

          {/* Estado del preview - controles */}
          <section className="card flex flex-col gap-2 ring-2 ring-amber-300">
            <p className="text-xs uppercase tracking-wider text-amber-700">
              Controles del preview
            </p>
            <p className="text-xs text-leaf-600">
              Cambia el estado para ver como se ve tu app a tus clientes en cada situacion.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  setPreviewState("normal");
                  setMockTickets(3);
                }}
                className={`rounded-full px-3 py-2 text-xs font-medium ${
                  previewState === "normal"
                    ? "bg-leaf-700 text-white"
                    : "bg-white text-leaf-700 ring-1 ring-leaf-200"
                }`}
              >
                En progreso
              </button>
              <button
                onClick={() => setPreviewState("complete")}
                className={`rounded-full px-3 py-2 text-xs font-medium ${
                  previewState === "complete"
                    ? "bg-leaf-700 text-white"
                    : "bg-white text-leaf-700 ring-1 ring-leaf-200"
                }`}
              >
                Reward listo
              </button>
              <button
                onClick={() => setPreviewState("voucher")}
                className={`rounded-full px-3 py-2 text-xs font-medium ${
                  previewState === "voucher"
                    ? "bg-leaf-700 text-white"
                    : "bg-white text-leaf-700 ring-1 ring-leaf-200"
                }`}
              >
                Con voucher
              </button>
            </div>
            {previewState === "normal" && (
              <div className="mt-1">
                <label className="text-[11px] text-leaf-600">
                  Tickets actuales: {mockTickets} / {loyalty.ticketsForReward}
                </label>
                <input
                  type="range"
                  min={0}
                  max={loyalty.ticketsForReward}
                  value={mockTickets}
                  onChange={(e) => setMockTickets(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
          </section>

          {/* Voucher mock */}
          {showVoucher && (
            <section
              className="rounded-3xl p-5 shadow-soft"
              style={{ backgroundColor: tenant.themePrimary, color: "#ffffff" }}
            >
              <p className="text-xs uppercase tracking-wider text-white/80">Voucher activo</p>
              <h3 className="mt-1 font-display text-2xl font-semibold">10% de descuento</h3>
              <p className="mt-1 font-mono text-sm text-white/90">DEMOAMB10</p>
              <p className="mt-2 text-xs text-white/80">
                Valido hasta las 23:59. Muestraselo al pagar.
              </p>
            </section>
          )}

          {/* Codigo embajador */}
          {!showVoucher && (
            <section className="card flex flex-col gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-leaf-600">Codigo de embajador</p>
                <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                  ¿Tienes un codigo de descuento?
                </h3>
                <p className="mt-1 text-sm text-leaf-700">
                  Actívalo antes de pagar para tener descuento.
                </p>
              </div>
              <div className="flex gap-2">
                <input
                  disabled
                  placeholder={`${tenant.tenantId.toUpperCase().slice(0, 6)}-XXXX`}
                  className="input cursor-not-allowed font-mono text-sm uppercase opacity-60"
                />
                <button disabled className="btn-primary cursor-not-allowed px-5 opacity-60">
                  Activar
                </button>
              </div>
            </section>
          )}

          {/* Upload boleta - deshabilitado */}
          <section className="card flex flex-col gap-3 opacity-90">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Despues de tu compra</p>
              <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                Sube tu boleta para sumar un punto
              </h3>
              <p className="mt-1 text-sm text-leaf-700">
                Saca foto de la boleta donde se vea el RUT del comercio.
              </p>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/50 py-8 text-leaf-700">
              <svg
                className="h-8 w-8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span className="text-sm font-medium">Sacar foto de la boleta</span>
              <span className="text-xs text-leaf-500">No disponible en preview</span>
            </div>
          </section>

          {/* Ultimas compras mock */}
          <section className="card flex flex-col gap-2">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Ultimas compras</p>
            {[
              { amount: 4490, status: "approved", date: "29 may" },
              { amount: 3490, status: "approved", date: "27 may" },
              { amount: 5990, status: "pending_admin_review", date: "26 may" }
            ].map((p, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-t border-leaf-100 pt-2 first:border-0 first:pt-0"
              >
                <div>
                  <p className="text-sm font-medium text-leaf-950">
                    {new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0
                    }).format(p.amount)}
                  </p>
                  <p className="text-[11px] text-leaf-500">{p.date}</p>
                </div>
                <PurchaseStatusBadge status={p.status} />
              </div>
            ))}
          </section>

          {/* Ambassador CTA */}
          <section className="rounded-3xl bg-gradient-to-br from-leaf-50 to-leaf-100/60 p-5 ring-1 ring-leaf-200">
            <p className="text-xs uppercase tracking-wider text-leaf-700">Programa de embajadores</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-leaf-950">
              ¿Tienes seguidores en Instagram?
            </h3>
            <p className="mt-1 text-sm text-leaf-800">
              Vuélvete embajador de {tenant.name} y gana comision por cada compra que refieras.
            </p>
            <button disabled className="btn-secondary mt-3 w-full cursor-not-allowed opacity-70">
              Postular a embajador
            </button>
          </section>
        </div>
      </main>
    </div>
  );
}

function PurchaseStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    approved: { bg: "bg-leaf-100", fg: "text-leaf-800", label: "Aprobada" },
    pending_admin_review: { bg: "bg-amber-100", fg: "text-amber-800", label: "En revisión" },
    rejected: { bg: "bg-red-100", fg: "text-red-700", label: "Rechazada" }
  };
  const s = styles[status] ?? { bg: "bg-gray-100", fg: "text-gray-600", label: status };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}
